import { ChangeDetectionStrategy, Component, Input, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ActionPreviewOutcomeSection } from '../../../../../services/gable/gable.service';

export type ActionPreviewConfidence = 'high' | 'review' | 'low';

/**
 * `outcome` section — the "If you confirm" decision surface (A4). Renders as a
 * light gray inset panel: a header row with the `label` and a confidence badge
 * (fed down from the card's `why` section), then the concrete `effect` line
 * prefixed with a check-circle. The affected-record line was hoisted to the
 * card's context row, so it is no longer rendered here. Non-editable.
 */
@Component({
    selector: 'app-ap-outcome-section',
    templateUrl: './outcome-section.component.html',
    styleUrls: ['./outcome-section.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActionPreviewOutcomeSectionComponent {
    @Input({ required: true }) section!: ActionPreviewOutcomeSection;

    /** Confidence tier extracted from the card's `why` section; null → no badge. */
    @Input() confidence: ActionPreviewConfidence | null = null;

    /**
     * Live effect text reflecting an in-flight edit (the user changed the
     * status_picker value) — computed by the card; replaces `section.effect`.
     */
    @Input() effectOverride: string | null = null;

    /** The card's lead line: a small green alert in place of the panel. */
    @Input() summary = false;

    get effectText(): string {
        return this.effectOverride ?? this.section.effect;
    }

    /** Fixed badge copy per confidence tier; null when no badge should show. */
    get confidenceLabel(): string | null {
        switch (this.confidence) {
            case 'high':
                return 'High confidence';
            case 'review':
                return 'Needs your judgment';
            case 'low':
                return 'Needs your call';
            default:
                return null;
        }
    }
}
